/**
 * Tutoriels vidéo « VisionPOS en 1 minute » (27 vidéos, dont 6 restaurant et 2 monnayeur).
 *
 * Fichiers servis par le site (public/tutos/<id>/), hors bundle et hors
 * précache du service worker (globIgnores dans vite.config.ts) : chaque mp4
 * fait 3 à 4 Mo et doit être lu en streaming (requêtes Range → 206).
 * Incrémenter TUTO_MEDIA_VERSION à chaque remplacement d'une vidéo.
 */

export const TUTO_MEDIA_VERSION = 4;

export type TutoId =
  | 'visionpos-tuto-premier-jour'
  | 'visionpos-tuto-mot-de-passe-acces'
  | 'visionpos-tuto-verrouiller-hors-ligne'
  | 'visionpos-tuto-encaisser'
  | 'visionpos-tuto-remise-fractionne'
  | 'visionpos-tuto-carte-tpe'
  | 'visionpos-tuto-avoir'
  | 'visionpos-tuto-duplicata-note-de-frais'
  | 'visionpos-tuto-cloture'
  | 'visionpos-tuto-cloture-oubliee-ecart'
  | 'visionpos-tuto-articles-stocks'
  | 'visionpos-tuto-modifier-article'
  | 'visionpos-tuto-pda'
  | 'visionpos-tuto-clients-fidelite'
  | 'visionpos-tuto-bl-facturation'
  | 'visionpos-tuto-devis-bl-facture'
  | 'visionpos-tuto-imprimante-tpe'
  | 'visionpos-tuto-imprimante-ajouter'
  | 'visionpos-tuto-monnayeur'
  | 'visionpos-tuto-monnayeur-gestion'
  | 'visionpos-tuto-tableau-de-bord'
  | 'visionpos-tuto-certificat-fec'
  | 'visionpos-tuto-plan-de-salle'
  | 'visionpos-tuto-tablette-serveur'
  | 'visionpos-tuto-ecran-cuisine'
  | 'visionpos-tuto-encaisser-table'
  | 'visionpos-tuto-ardoise-bar'
  | 'visionpos-tuto-sur-place-emporter';

export type VideoCategoryId =
  'getting-started' | 'caisse' | 'chr' | 'products' | 'clients' | 'reports' | 'config';

export type Tuto = {
  readonly id: TutoId;
  readonly title: string;
  readonly description: string;
  readonly category: VideoCategoryId;
  readonly durationS: number;
};

/** Ordre de la série (chaque vidéo annonce la suivante). */
export const TUTOS: readonly Tuto[] = [
  {
    id: 'visionpos-tuto-premier-jour',
    title: 'Premier jour',
    description: 'Ouvrir la caisse, découvrir l’écran et faire sa première vente en confiance.',
    category: 'getting-started',
    durationS: 48,
  },
  {
    id: 'visionpos-tuto-mot-de-passe-acces',
    title: 'Mot de passe et accès',
    description: 'Mot de passe oublié, e-mail de réinitialisation et accès rapide des salariés.',
    category: 'getting-started',
    durationS: 61,
  },
  {
    id: 'visionpos-tuto-verrouiller-hors-ligne',
    title: 'Verrouiller la caisse et hors ligne',
    description:
      'Verrouiller la caisse sans la clôturer et continuer à vendre quand Internet coupe.',
    category: 'getting-started',
    durationS: 54,
  },
  {
    id: 'visionpos-tuto-encaisser',
    title: 'Encaisser une vente',
    description:
      'Ajouter des articles, appliquer une remise et encaisser en espèces, carte ou en plusieurs moyens.',
    category: 'caisse',
    durationS: 55,
  },
  {
    id: 'visionpos-tuto-remise-fractionne',
    title: 'Remise et paiement fractionné',
    description:
      'Appliquer une remise en pourcentage et régler une vente avec plusieurs moyens de paiement.',
    category: 'caisse',
    durationS: 54,
  },
  {
    id: 'visionpos-tuto-carte-tpe',
    title: 'Payer par carte avec le TPE',
    description:
      'Envoyer le montant au TPE relié à la caisse, paiement approuvé ou refusé, et que faire ensuite.',
    category: 'caisse',
    durationS: 60,
  },
  {
    id: 'visionpos-tuto-avoir',
    title: 'Annuler et faire un avoir',
    description: 'Retrouver un ticket, faire un retour ou un avoir, réimprimer un duplicata.',
    category: 'caisse',
    durationS: 47,
  },
  {
    id: 'visionpos-tuto-duplicata-note-de-frais',
    title: 'Duplicata et note de frais',
    description:
      'Retrouver une vente, réimprimer un duplicata et éditer une note de frais pour un client.',
    category: 'caisse',
    durationS: 42,
  },
  {
    id: 'visionpos-tuto-cloture',
    title: 'Clôture Z et clôtures',
    description:
      'Fermer la caisse en fin de journée, compter le fond de caisse et retrouver vos clôtures.',
    category: 'caisse',
    durationS: 45,
  },
  {
    id: 'visionpos-tuto-cloture-oubliee-ecart',
    title: 'Clôture oubliée et écart de caisse',
    description:
      'Le rappel du soir, la clôture automatique de la nuit et comment traiter un écart de caisse.',
    category: 'caisse',
    durationS: 54,
  },
  {
    id: 'visionpos-tuto-articles-stocks',
    title: 'Articles et stocks',
    description:
      'Créer un article (prix, TVA, code-barres), le ranger par catégorie et suivre le stock.',
    category: 'products',
    durationS: 43,
  },
  {
    id: 'visionpos-tuto-modifier-article',
    title: 'Modifier un article',
    description: 'Changer le prix, le stock, la catégorie, le code-barres ou la TVA d’un article.',
    category: 'products',
    durationS: 57,
  },
  {
    id: 'visionpos-tuto-pda',
    title: 'Vision Stock : prise en main au PDA',
    description:
      'Mode rafale (+1 par scan, codes inconnus à traiter après), choix au scan, pavé géant pour remplacer ou ajouter, réception, inventaire guidé, codes inconnus, étiquettes et mouvements.',
    category: 'products',
    durationS: 228,
  },
  {
    id: 'visionpos-tuto-clients-fidelite',
    title: 'Clients et fidélité',
    description:
      'Créer une fiche client, l’associer à une vente et faire vivre le programme de fidélité.',
    category: 'clients',
    durationS: 43,
  },
  {
    id: 'visionpos-tuto-bl-facturation',
    title: 'BL et facture du mois',
    description:
      'Étiquettes code-barres, bon d’enlèvement en caisse (« Pris par », chantier) et facture récapitulative de fin de mois par client.',
    category: 'clients',
    durationS: 165,
  },
  {
    id: 'visionpos-tuto-devis-bl-facture',
    title: 'Devis, BL et facture Lekompte',
    description:
      'Le parcours pro complet : devis, bon de livraison, paiement immédiat ou plus tard, facture Lekompte et encaissement ensuite.',
    category: 'clients',
    durationS: 163,
  },
  {
    id: 'visionpos-tuto-imprimante-tpe',
    title: 'Imprimante et TPE',
    description:
      'Brancher l’imprimante ticket, tester l’impression et relier le terminal de paiement.',
    category: 'config',
    durationS: 55,
  },
  {
    id: 'visionpos-tuto-imprimante-ajouter',
    title: 'Ajouter une imprimante',
    description: 'Imprimante intégrée, imprimante réseau détectée automatiquement et page de test.',
    category: 'config',
    durationS: 51,
  },
  {
    id: 'visionpos-tuto-monnayeur',
    title: 'Encaisser avec le monnayeur',
    description:
      'Bouton Monnayeur, annulation, carte refusée, monnaie insuffisante, Espèces tiroir et paiement fractionné. Disponible sur demande.',
    category: 'config',
    durationS: 100,
  },
  {
    id: 'visionpos-tuto-monnayeur-gestion',
    title: 'Le monnayeur au quotidien',
    description:
      'Recharge du matin, paiements, remboursement, vidage du soir et clôture de la machine, sans oublier la clôture Z.',
    category: 'config',
    durationS: 150,
  },
  {
    id: 'visionpos-tuto-tableau-de-bord',
    title: 'Tableau de bord',
    description:
      'Lire le chiffre du jour, les meilleures ventes et les indicateurs clés de votre commerce.',
    category: 'reports',
    durationS: 48,
  },
  {
    id: 'visionpos-tuto-certificat-fec',
    title: 'Certificat et export comptable',
    description:
      'Télécharger le certificat de la caisse en cas de contrôle et envoyer le FEC à votre comptable.',
    category: 'reports',
    durationS: 57,
  },
  {
    id: 'visionpos-tuto-plan-de-salle',
    title: 'Le plan de salle',
    description:
      'Créer son plan, poser les tables, régler les couverts et voir d’un coup d’œil les tables libres ou occupées.',
    category: 'chr',
    durationS: 58,
  },
  {
    id: 'visionpos-tuto-tablette-serveur',
    title: 'La tablette serveur',
    description:
      'Ouvrir une table, prendre la commande par service, l’envoyer en cuisine ou en caisse, puis libérer la table.',
    category: 'chr',
    durationS: 58,
  },
  {
    id: 'visionpos-tuto-ecran-cuisine',
    title: 'L’écran cuisine',
    description:
      'Suivre les bons en attente, en préparation et prêts, filtrer par poste et repérer les plats réclamés.',
    category: 'chr',
    durationS: 49,
  },
  {
    id: 'visionpos-tuto-encaisser-table',
    title: 'Encaisser une table',
    description:
      'Encaisser toute la table, une sélection d’articles ou partager l’addition en parts égales.',
    category: 'chr',
    durationS: 49,
  },
  {
    id: 'visionpos-tuto-ardoise-bar',
    title: 'Les ardoises du bar',
    description:
      'Ouvrir une ardoise au nom du client avec un plafond, ajouter des consommations et encaisser.',
    category: 'chr',
    durationS: 42,
  },
  {
    id: 'visionpos-tuto-sur-place-emporter',
    title: 'Sur place ou à emporter',
    description:
      'Activer les modes de service et appliquer automatiquement la bonne TVA sur place ou à emporter.',
    category: 'chr',
    durationS: 48,
  },
];

export type TutoSources = {
  readonly src: string;
  readonly poster: string;
};

/** Vidéo verticale (9:16) sur téléphone en portrait, paysage (16:9) sinon. */
export function tutoSources(id: TutoId, portrait: boolean): TutoSources {
  const format = portrait ? 'mobile' : 'paysage';
  const base = `/tutos/${id}/${format}`;
  return {
    src: `${base}.mp4?v=${TUTO_MEDIA_VERSION}`,
    poster: `${base}.jpg?v=${TUTO_MEDIA_VERSION}`,
  };
}

export function findTuto(id: TutoId): Tuto {
  const tuto = TUTOS.find((t) => t.id === id);
  if (!tuto) throw new Error(`Tutoriel inconnu : ${id}`);
  return tuto;
}

/** 48 → « 0:48 » */
export function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}
